import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Utensils,
  Receipt,
  ChefHat,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { restaurantApi } from "@/lib/restaurantApi";

/**
 * Fixed bottom navigation for tablet/mobile (hidden ≥ lg). 4 fixed tabs with
 * lucide icons, active green state, badge counters fetched lightly every 30 s.
 * Respects iOS safe-area inset.
 */
export default function BottomNav() {
  const { t } = useTranslation();
  const { tenant, isPlatform } = useAuth();
  const location = useLocation();
  const [counts, setCounts] = useState({ orders: 0, kitchen: 0 });

  // Only show inside a tenant context that has the restaurant module
  const visible =
    !isPlatform &&
    (tenant?.enabled_modules || []).some((m) =>
      ["restaurant", "restaurant_core", "pos"].includes(m)
    );

  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    const load = async () => {
      try {
        const [orders, tickets] = await Promise.all([
          restaurantApi
            .orders({ status: "sent_to_kitchen,in_preparation,ready,served", limit: 100 })
            .then((r) => r.data?.items || [])
            .catch(() => []),
          restaurantApi
            .tickets()
            .then((r) => r.data?.items || [])
            .catch(() => []),
        ]);
        if (!cancelled) {
          setCounts({
            orders: orders.filter((o) =>
              ["sent_to_kitchen", "in_preparation", "ready", "served"].includes(o.status)
            ).length,
            kitchen: tickets.length,
          });
        }
      } catch (_) {}
    };
    load();
    const id = setInterval(load, 30000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [visible]);

  if (!visible) return null;

  const TABS = [
    {
      to: "/dashboard",
      label: t("nav_dashboard", { defaultValue: "Dashboard" }),
      icon: LayoutDashboard,
      match: (p) => p === "/dashboard" || p === "/",
      testId: "bottomnav-dashboard",
    },
    {
      to: "/restaurant/tables",
      label: t("restaurant_nav.tables", { defaultValue: "Sala" }),
      icon: Utensils,
      match: (p) => p.startsWith("/restaurant/tables"),
      testId: "bottomnav-tables",
    },
    {
      to: "/restaurant/orders",
      label: t("restaurant_nav.orders", { defaultValue: "Ordini" }),
      icon: Receipt,
      match: (p) => p.startsWith("/restaurant/orders") || p.startsWith("/restaurant/pos"),
      badge: counts.orders,
      testId: "bottomnav-orders",
    },
    {
      to: "/restaurant/kitchen",
      label: t("restaurant_nav.kitchen", { defaultValue: "Cucina" }),
      icon: ChefHat,
      match: (p) => p.startsWith("/restaurant/kitchen"),
      badge: counts.kitchen,
      testId: "bottomnav-kitchen",
    },
  ];

  return (
    <nav
      data-testid="bottom-nav"
      aria-label="Navigazione principale"
      className="lg:hidden fixed left-0 right-0 bottom-0 z-30 bg-white border-t border-[#E2E8F0] shadow-[0_-2px_12px_-4px_rgba(17,24,21,0.06)]"
      style={{
        paddingBottom: "env(safe-area-inset-bottom, 0px)",
      }}
    >
      <ul className="grid grid-cols-4 h-[64px]">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          const active = tab.match(location.pathname);
          return (
            <li key={tab.to} className="flex">
              <Link
                to={tab.to}
                data-testid={tab.testId}
                aria-current={active ? "page" : undefined}
                className={`flex-1 flex flex-col items-center justify-center gap-0.5 min-w-[44px] relative active:bg-[#F2F4F2] transition-colors ${
                  active ? "text-[#102A43]" : "text-[#475569]"
                }`}
              >
                <span
                  className={`grid place-items-center w-9 h-7 rounded-lg ${
                    active ? "bg-[#E0F2F1]" : ""
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {!!tab.badge && tab.badge > 0 && (
                    <span
                      className="absolute top-2 right-[20%] min-w-[16px] h-[16px] px-1 rounded-full bg-[#B91C1C] text-white text-[9px] font-bold leading-[16px] text-center tabular-nums"
                      data-testid={`${tab.testId}-badge`}
                    >
                      {tab.badge > 99 ? "99+" : tab.badge}
                    </span>
                  )}
                </span>
                <span className="text-[10px] font-medium leading-none">{tab.label}</span>
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
